import {
  BallFinalKinds,
  BaseballLineupPosition,
  Dexterity,
  IApiCatcherThrowTag,
  IApiPitchTag,
  IApiPlayerExtended,
  IApiTaggingPlayers,
  TopBottom,
  baseballLogicService,
  constants,
} from '@trackman/baseball-shared-components';
import { emptyPlayer } from 'constants/Constants';
import { SessionType } from 'enums/SessionType';
import { IReviewDataRow, IReviewNote, IStatus } from 'interfaces/review/IReviewDataRow';
import { IReviewInning } from 'interfaces/review/IReviewInning';
import { IReviewCatcher, IReviewPlayer } from 'interfaces/review/IReviewPlayer';
import { IApiPlayWithBallFinals } from 'interfaces/session/IApiPlayWithBallFinals';
import { IApiSessionMetadataUpdated } from 'interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import { IApiPlayLineup, IApiStadiumPlayModel } from 'interfaces/tagging/ApiModels/IApiStadiumPlayModel';
import { IChallengeTag } from 'interfaces/tagging/ApiModels/IChallengeTag';
import moment from 'moment';
import { isHomeTeamBatting } from 'services/helper-services/baseballLogicHelpers';
import mappingHelpers from 'services/helper-services/mappingHelpers/mappingHelpers';
import { IApiBallFinal } from '../../../interfaces/broadcast/IApiBallFinal';
import { ReviewDataRowMetrics } from './ReviewDataRowMetrics';

type DefensivePositions = {
  firstBaseman: IApiPlayerExtended;
  secondBaseman: IApiPlayerExtended;
  thirdBaseman: IApiPlayerExtended;
  shortStopper: IApiPlayerExtended;
  leftFielder: IApiPlayerExtended;
  centerFielder: IApiPlayerExtended;
  rightFielder: IApiPlayerExtended;
};

export class ReviewDataRowProps extends ReviewDataRowMetrics implements IReviewDataRow {
  firstBaseman: IApiPlayerExtended;

  secondBaseman: IApiPlayerExtended;

  thirdBaseman: IApiPlayerExtended;

  shortStopper: IApiPlayerExtended;

  leftFielder: IApiPlayerExtended;

  centerFielder: IApiPlayerExtended;

  rightFielder: IApiPlayerExtended;

  firstBase: IApiPlayerExtended;

  secondBase: IApiPlayerExtended;

  thirdBase: IApiPlayerExtended;

  id: string;

  playId?: string;

  status: IStatus;

  note: IReviewNote;

  playSequenceNumber?: number;

  sequenceNumber: number;

  pitcher: IReviewPlayer;

  catcher: IReviewCatcher;

  pitcherDexterity: Dexterity;

  batter: IReviewPlayer;

  batterDexterity: Dexterity;

  inning: IReviewInning;

  balls: number;

  strikes: number;

  outs: number;

  taggedPitchType: string;

  pitchCall: string;

  korbb: string;

  hitType: string;

  playResult?: string;

  outsOnPlay: number;

  runsScored: number;

  trackStartTimeAutoGenerated: boolean;

  isChanged: boolean;

  isAddedManually: boolean;

  isWarmup: boolean;

  isDeleted: boolean;

  homeTeamScore: number;

  awayTeamScore: number;

  pitchBallFinalTrackId?: string;

  hitBallFinalTrackId?: string;

  catcherThrowBallFinalTrackId?: string;

  pAofInning?: number;

  pitchOfPA?: number;

  catcherThrowTag: IApiCatcherThrowTag | null;

  challengeTag?: IChallengeTag | null;

  playLineup: IApiPlayLineup;

  trackStartTimeLocal: string | null;

  trackStartTimeUtc: string | undefined | null;

  displayIndex: number;

  homeTeamName: string;

  awayTeamName: string;

  fieldingSequence: {
    fielder1: IApiPlayerExtended;
    fielder2: IApiPlayerExtended;
    fielder3: IApiPlayerExtended;
  };

  constructor(playData: IApiPlayWithBallFinals, homeTeamName: string, awayTeamName: string) {
    const { play, ballFinals, isChanged } = playData;

    super(ballFinals);
    this.id = play.id;
    this.playId = play.playId;
    this.balls = play.gameState.balls;
    this.batterDexterity = play.players.batter.battingHandedness;
    this.hitType = play.hitTag.hitType;
    this.inning = {
      inningNumber: play.gameState.inning,
      isTopInning: play.gameState.topBottom === TopBottom.Top,
    };
    this.korbb = play.kOrBB;
    this.note = {
      note: play.note,
      isResolved: play.customMetadata?.isNoteResolved || false,
    };
    this.outs = play.gameState.outs;
    this.outsOnPlay = play.playResult.outs;
    this.pitchCall = play.pitchTag.pitchCall;
    this.playSequenceNumber = play.taggerBehavior.playSequenceNumber;
    this.sequenceNumber = play.taggerBehavior.sequenceNumber;
    this.batter = {
      ...play.players.batter,
      isHomeTeam: play.gameState.isHomeTeamBatting,
      name: play.players.batter.nameRef,
      teamName: play.gameState.isHomeTeamBatting ? homeTeamName : awayTeamName,
    };
    this.pitcher = {
      ...play.players.pitcher,
      isHomeTeam: !play.gameState.isHomeTeamBatting,
      name: play.players.pitcher.nameRef,
      teamName: play.gameState.isHomeTeamBatting ? awayTeamName : homeTeamName,
    };
    this.catcher = {
      ...play.players.catcher,
      isHomeTeam: !play.gameState.isHomeTeamBatting,
      name: play.players.catcher.nameRef,
      teamName: play.gameState.isHomeTeamBatting ? awayTeamName : homeTeamName,
    };
    this.pitcherDexterity = play.players.pitcher.pitchingHandedness;
    this.strikes = play.gameState.strikes;
    this.taggedPitchType = play.pitchTag.pitchType;
    this.isChanged = isChanged;
    this.runsScored = play.playResult.runs;
    this.isAddedManually = play.taggerBehavior.manuallyAddedByTagger;
    this.isWarmup = play.taggerBehavior.isWarmup;
    this.isDeleted = play.taggerBehavior.isDeleted;
    this.homeTeamScore = play.gameState.homeTeamScore;
    this.awayTeamScore = play.gameState.awayTeamScore;
    this.playResult = play.playResult.result;
    this.pitchBallFinalTrackId = play.pitchTag.ballFinalTrackId;
    this.hitBallFinalTrackId = play.hitTag?.ballFinalTrackId;
    this.catcherThrowBallFinalTrackId = play.catcherThrowTag?.ballFinalTrackId;
    this.pitchOfPA = play.taggerBehavior.pitchOfPA;
    this.pAofInning = play.taggerBehavior.pAofInning;
    this.status = {
      errors: [],
      status: '',
    };
    this.catcherThrowTag = play.catcherThrowTag;
    this.challengeTag = play.challengeTag;
    this.playLineup = play.playLineup;
    this.trackStartTimeUtc = play.trackStartTimeUtc;
    this.displayIndex = -1;
    this.mergeBallFinalData(ballFinals);
    this.trackStartTimeLocal = this.trackStartTimeUtc ? moment(this.trackStartTimeUtc).local().format() : null;
    this.homeTeamName = homeTeamName;
    this.awayTeamName = awayTeamName;
    this.firstBase = play.baseRunners?.firstBase;
    this.secondBase = play.baseRunners?.secondBase;
    this.thirdBase = play.baseRunners?.thirdBase;
    this.firstBaseman = play.players.firstBaseman;
    this.secondBaseman = play.players.secondBaseman;
    this.thirdBaseman = play.players.thirdBaseman;
    this.shortStopper = play.players.shortStopper;
    this.leftFielder = play.players.leftFielder;
    this.centerFielder = play.players.centerFielder;
    this.rightFielder = play.players.rightFielder;
    this.fieldingSequence = play.fieldingSequence;
    this.trackStartTimeAutoGenerated = play.trackStartTimeAutoGenerated;
  }

  private mergeBallFinalData(ballFinals: IApiBallFinal[]) {
    ballFinals.forEach((bf) => {
      switch (bf.kind) {
        case BallFinalKinds.Pitch:
          if (this.pitchBallFinalTrackId !== bf.trackId) {
            this.pitchBallFinalTrackId = bf.trackId;
            this.isChanged = true;
          }
          if (this.trackStartTimeUtc !== bf.trackStartTime) {
            this.trackStartTimeUtc = bf.trackStartTime;
            this.isChanged = true;
          }
          break;
        case BallFinalKinds.Hit:
          if (this.hitBallFinalTrackId !== bf.trackId) {
            this.hitBallFinalTrackId = bf.trackId;
            this.isChanged = true;
          }
          break;
        case BallFinalKinds.CatcherThrow:
          if (this.catcherThrowBallFinalTrackId !== bf.trackId) {
            this.catcherThrowBallFinalTrackId = bf.trackId;
            this.isChanged = true;
          }
          break;
      }
    });
  }

  toApiPlay(session: IApiSessionMetadataUpdated): IApiPlayWithBallFinals {
    const play = {
      ballFinals: [],
      isChanged: this.isChanged,
      play: {
        id: this.id,
        sessionId: session.sessionId,
        playId: this.playId,
        taggerBehavior: {
          manuallyAddedByTagger: this.isAddedManually,
          isWarmup: this.isWarmup,
          playSequenceNumber: this.playSequenceNumber,
          sequenceNumber: this.sequenceNumber,
          isDeleted: this.isDeleted,
          pitchOfPA: this.pitchOfPA,
          pAofInning: this.pAofInning,
        },
        players: this.reviewRowToApiTaggingPlayersMap(), // TODO: Fix types in IApiStadiumPlayModel or function reviewRowToApiTaggingPlayersMap()
        baseRunners: {
          firstBase: this.firstBase,
          secondBase: this.secondBase,
          thirdBase: this.thirdBase,
        },
        fieldingSequence: this.fieldingSequence,
        gameState: {
          homeTeamScore: this.homeTeamScore,
          awayTeamScore: this.awayTeamScore,
          isHomeTeamBatting: !this.inning.isTopInning,
          topBottom: this.inning.isTopInning ? TopBottom.Top : TopBottom.Bottom,
          balls: this.balls,
          strikes: this.strikes,
          outs: this.outs,
          inning: this.inning.inningNumber,
        },
        pitchTag: {
          ballFinalTrackId: this.pitchBallFinalTrackId,
          pitchCall: this.pitchCall,
          pitchType: this.taggedPitchType,
          pitcherSet: constants.undefinedValue,
        } as IApiPitchTag,
        hitTag: {
          hitType: this.hitType,
          ballFinalTrackId: this.hitBallFinalTrackId,
        },
        catcherThrowTag: this.reviewRowToApiCatcherThrowTagMap(),
        challengeTag: this.challengeTag,
        playResult: {
          outs: this.outsOnPlay,
          runs: this.runsScored,
          result: this.playResult,
        },
        note: this.note.note,
        kOrBB: this.korbb,
        pitchingTeam:
          isHomeTeamBatting(this.inning.isTopInning) && session.sessionType !== SessionType.BattingPractice
            ? mappingHelpers.teamWithLineupToTeamMap(session.awayTeam)
            : mappingHelpers.teamWithLineupToTeamMap(session.homeTeam),
        battingTeam:
          isHomeTeamBatting(this.inning.isTopInning) || session.sessionType === SessionType.BattingPractice
            ? mappingHelpers.teamWithLineupToTeamMap(session.homeTeam)
            : mappingHelpers.teamWithLineupToTeamMap(session.awayTeam),
        customMetadata: {
          isNoteResolved: this.note.isResolved,
          isValidated: true,
          taggingFlowState: '',
        },
        playLineup: this.playLineup,
        trackStartTimeUtc: this.getTrackStartTimeUtc(),
        trackStartTimeAutoGenerated: this.trackStartTimeAutoGenerated,
      } as IApiStadiumPlayModel,
    } as IApiPlayWithBallFinals;
    return play;
  }

  get isHomeTeamBatting() {
    return baseballLogicService.isHomeTeamBatting(this.inning.isTopInning);
  }

  changeTeam(newTeamName: string, isHomeTeam: boolean) {
    if (this.isHomeTeamBatting === isHomeTeam) {
      this.batter = { ...emptyPlayer, isHomeTeam: true, name: '', teamName: newTeamName, birthDate: null, ids: null };
    } else {
      this.pitcher = { ...emptyPlayer, isHomeTeam: false, name: '', teamName: newTeamName, birthDate: null, ids: null };
      this.catcher = {
        ...emptyPlayer,
        isHomeTeam: false,
        name: '',
        teamName: newTeamName,
        pitchingHandedness: Dexterity.Undefined,
        birthDate: null,
        ids: null,
      };
    }
    this.isChanged = true;
  }

  private getTrackStartTimeUtc() {
    if (this.isAddedManually) {
      return this.trackStartTimeLocal ? moment.utc(this.trackStartTimeLocal).format() : null;
    }
    return this.trackStartTimeUtc;
  }

  private reviewRowToApiTaggingPlayersMap() {
    // @ts-ignore
    const players = {
      pitcher: {
        ...this.pitcher,
        id: this.pitcher.id ? this.pitcher.id : constants.emptyGuid,
        nameRef: this.pitcher.name,
        position: this.pitcher.position,
        pitchingHandedness: this.pitcherDexterity,
        foreignIds: this.pitcher.foreignIds,
        isPlaceholder: this.pitcher.isPlaceholder,
      },
      batter: {
        ...this.batter,
        id: this.batter.id ? this.batter.id : constants.emptyGuid,
        nameRef: this.batter.name,
        position: this.batter.position,
        battingHandedness: this.batterDexterity,
        foreignIds: this.batter.foreignIds,
        isPlaceholder: this.batter.isPlaceholder,
      },
      catcher: {
        ...this.catcher,
        id: this.catcher.id ? this.catcher.id : constants.emptyGuid,
        nameRef: this.catcher.name,
        position: this.catcher.position,
        foreignIds: this.catcher.foreignIds,
        pitchingHandedness: this.catcher.pitchingHandedness,
        isPlaceholder: this.catcher.isPlaceholder,
      },
      firstBaseman: {
        ...this.firstBaseman,
        position: BaseballLineupPosition.FirstBase,
      },
      secondBaseman: {
        ...this.secondBaseman,
        position: BaseballLineupPosition.SecondBase,
      },
      thirdBaseman: {
        ...this.thirdBaseman,
        position: BaseballLineupPosition.ThirdBase,
      },
      shortStopper: {
        ...this.shortStopper,
        position: BaseballLineupPosition.Shortstop,
      },
      leftFielder: {
        ...this.leftFielder,
        position: BaseballLineupPosition.LeftField,
      },
      centerFielder: {
        ...this.centerFielder,
        position: BaseballLineupPosition.CenterField,
      },
      rightFielder: {
        ...this.rightFielder,
        position: BaseballLineupPosition.RightField,
      },
    } as IApiTaggingPlayers & DefensivePositions;

    return players;
  }

  private reviewRowToApiCatcherThrowTagMap() {
    return {
      ballFinalTrackId: this.catcherThrowBallFinalTrackId,
      thrownPosition: this.catcherThrowTag ? this.catcherThrowTag.thrownPosition : constants.undefinedValue,
      fielder: this.catcherThrowTag ? this.catcherThrowTag.fielder : { ...constants.emptyPlayer },
    };
  }
}
